import { supabase } from "./supabase";
import { loadCatalogBrands } from "./catalog-data";

const DEMAND_MONTH_REQUEST_KEY = "kitanego-demand-month-request";
let pendingDemandMonth = "";

export function requestDemandMonth(month: string): void {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return;
  pendingDemandMonth = month;
  try {
    window.sessionStorage.setItem(DEMAND_MONTH_REQUEST_KEY, month);
  } catch {
    // The in-memory request still carries the selection for this navigation.
  }
}

export function takeRequestedDemandMonth(): string {
  let month = pendingDemandMonth;
  pendingDemandMonth = "";
  try {
    month ||= window.sessionStorage.getItem(DEMAND_MONTH_REQUEST_KEY) ?? "";
    window.sessionStorage.removeItem(DEMAND_MONTH_REQUEST_KEY);
  } catch {
    // Session storage is optional; use the in-memory request when unavailable.
  }
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? month : "";
}

export type MonthlyWishlist = {
  id: string;
  month: string;
  categories: string[];
  products: string[];
  brands: string[];
  frequency: string;
  spendRange: string;
  priceInterest: string;
  createdAt: string;
  updatedAt: string;
};

export type MonthlyWishlistInput = Omit<MonthlyWishlist, "id" | "createdAt" | "updatedAt">;

type MonthlyWishlistRow = {
  id: string;
  wishlist_month: string;
  categories: string[] | null;
  products: string[] | null;
  brands: string[] | null;
  frequency: string;
  spend_range: string;
  price_interest: string;
  created_at: string;
  updated_at: string;
};

function normalize(row: MonthlyWishlistRow): MonthlyWishlist {
  return {
    id: row.id,
    month: row.wishlist_month.slice(0, 7),
    categories: row.categories ?? [],
    products: row.products ?? [],
    brands: row.brands ?? [],
    frequency: row.frequency,
    spendRange: row.spend_range,
    priceInterest: row.price_interest,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function loadSelectableBrandKeys() {
  // Monthly demand must follow the current active catalogue. Falling back to
  // bundled seed names can resurrect brands removed from the database.
  const brands = await loadCatalogBrands();
  return new Set(brands.map((brand) => brand.name.toLocaleLowerCase()));
}

export async function readMonthlyWishlists(): Promise<MonthlyWishlist[]> {
  const { data, error } = await supabase
    .from("monthly_wishlists")
    .select("id,wishlist_month,categories,products,brands,frequency,spend_range,price_interest,created_at,updated_at")
    .order("wishlist_month", { ascending: false });
  if (error) throw error;
  const selectableBrands = await loadSelectableBrandKeys();
  return ((data ?? []) as MonthlyWishlistRow[]).map(normalize).map((wishlist) => ({
    ...wishlist,
    brands: wishlist.brands.filter((brand) => selectableBrands.has(brand.toLocaleLowerCase())),
  }));
}

export async function saveMonthlyWishlist(input: MonthlyWishlistInput): Promise<MonthlyWishlist> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  const userId = sessionData.session?.user.id;
  if (!userId) throw new Error("Sign in again before saving your monthly wishlist.");
  const selectableBrands = await loadSelectableBrandKeys();
  const brands = input.brands.filter((brand) => selectableBrands.has(brand.toLocaleLowerCase()));

  const { data, error } = await supabase
    .from("monthly_wishlists")
    .upsert({
      auth_user_id: userId,
      wishlist_month: `${input.month}-01`,
      categories: input.categories,
      products: input.products,
      brands,
      frequency: input.frequency,
      spend_range: input.spendRange,
      price_interest: input.priceInterest,
      updated_at: new Date().toISOString(),
    }, { onConflict: "auth_user_id,wishlist_month" })
    .select("id,wishlist_month,categories,products,brands,frequency,spend_range,price_interest,created_at,updated_at")
    .single();
  if (error) throw error;
  return normalize(data as MonthlyWishlistRow);
}

export async function deleteMonthlyWishlist(id: string): Promise<void> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  const userId = sessionData.session?.user.id;
  if (!userId) throw new Error("Sign in again before deleting your monthly demand.");

  const { data, error } = await supabase
    .from("monthly_wishlists")
    .delete()
    .eq("id", id)
    .eq("auth_user_id", userId)
    .select("id")
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("This monthly demand could not be found or deleted.");
}
